import { DateTime } from 'luxon';
import { deepClone, deepCompareEQ, isSecondDocNewer, mergeConflictingDocs, mergeConflictingDocsPreferring } from '@cf-shared/deep-helpers';
import { getTransactionTimeMs } from '@cf-shared/data-model/pouch';
import { isLogRecordEntry } from '@cf-shared/data-model/user-log';
import { logEntryCopyRank, logEntryHoldsUserData } from '@cf-shared/data-model/log-entry-user-data';
import { WhenMomentUnreadable, mayHappenAgainSinceStored } from '@cf-shared/periodic-suppression';
import { readUsableRolloutPercent } from './config-record-resolve';

// ---------------------------------------------------------------------------
// #4314 Phase C — on-device conflict auto-resolve: the pure decisions.
//
// Everything below is deliberately free of PouchDB and Angular so it can be
// unit-tested under node. The resolver that acts on these decisions lives in
// database-wrapper-conflict-resolve.ts; the wiring lives in database-wrapper.ts.
// See design-docs/sync-integrity.md ("Phase C — On-Device Conflict Auto-Resolve").
// ---------------------------------------------------------------------------

/**
 * Documents that are never merged, only closed (#4314 R2, gate G1).
 *
 * The merge decides each field independently, taking it from one version or the
 * other. That is fine for a document that records what happened, and dangerous
 * for one whose parts have to agree with each other — nothing checks that the
 * pieces it picked still make sense together. The training plan holds a list of
 * exercise slots while each workout separately holds a list of which slots it
 * uses, so a field-by-field merge can leave a workout pointing at a slot that is
 * not in the list. The schedule has the same shape of problem. The day-marker
 * cache is different but also not worth merging: it is a scratchpad the app can
 * rebuild from scratch at any time, so mixing two versions is pure downside.
 *
 * The ids are written out rather than imported from database-wrapper.ts, which
 * cannot be loaded under node (its pouchdb-browser import references `self`).
 * `day-task-status-cache` is also in DEVICE_LOCAL_DOC_IDS there; the other two
 * are ordinary replicating singletons, so this is NOT the same list and must not
 * be replaced by it.
 */
export const CONFLICT_NO_MERGE_DOC_IDS: readonly string[] = [
  'day-task-status-cache',
  'currentplan',
  'schedule',
];

/** How far apart two branches' write times may be and still be merged (#4314 R2, gate G2). */
export const CONFLICT_MERGE_MAX_DIVERGENCE_DAYS = 14;

/**
 * How much ACTUAL WORK one resolver run may do before it stops (#4314 R6b.1).
 *
 * Work time excludes the deliberate pauses between documents, and that
 * distinction is load-bearing: with a ~50 ms yield per document a wall-clock
 * budget would be spent almost entirely on pauses and stop after a handful of
 * documents. Time rather than count is the limit because a count does not bound
 * how slow the app FEELS.
 */
export const CONFLICT_RESOLVE_WORK_BUDGET_MS = 1500;

/**
 * The enumerating sweep's OWN allowance, deliberately separate from the work
 * budget (#4314 R6b.1).
 *
 * **This is an observability threshold, not a cap.** Exceeding it logs; nothing
 * stops or shortens the sweep. The point of the separate number is that the
 * sweep is NOT charged to the resolver's work budget — see below for why that
 * would deadlock the drain — not that the sweep is bounded. It cannot easily be
 * bounded: stopping half way would hand the resolver an arbitrary subset of the
 * conflicted documents with no record of what was skipped.
 *
 * The sweep reads full document bodies across the whole priority window — on the
 * heavy accounts this feature exists for, thousands of them. Charging it to the
 * budget it feeds would deadlock the drain: the sweep alone exhausts the budget,
 * the run resolves nothing, and "a run that resolved nothing" then backs the
 * device off for 24 hours — every day, permanently, on exactly the accounts that
 * most need draining. Nothing would look broken; the unit tests would all pass.
 */
export const CONFLICT_RESOLVE_ENUMERATION_BUDGET_MS = 5000;

/** Hard ceiling on documents per run — a backstop for a pathological cheap-document
 *  case, not the normal stopping condition (the work budget is expected to bind first). */
export const CONFLICT_RESOLVE_MAX_DOCS_PER_RUN = 100;

/** Pause between documents so user saves can take the write mutex and the event loop
 *  can render. Mirrors the 50 ms the sync path already yields between chunks. */
export const CONFLICT_RESOLVE_YIELD_MS = 50;

/** How long after a scan the next daily scan may run. */
export const CONFLICT_SCAN_COOLDOWN_HOURS = 24;

/**
 * Is on-device conflict auto-resolve enabled for this user? (#4314 R5)
 *
 * The bucket is keyed on the USER id, not the device: both of a user's devices
 * have to be in or out together, or they would fight and never converge. The
 * test is strictly `<`, so raising the percent only ever ADDS users — nobody is
 * ever taken back out. The prefix keeps this cohort uncorrelated with the Paddle
 * ramp that shares the same hash, so the two ramps do not select the same people.
 *
 * Every unusable percent — missing, a boolean, not a number, negative, above 100
 * — reads as 0, so every failure mode fails to DISABLED rather than switching the
 * resolver on by accident.
 */
export function shouldAutoResolveConflicts(
  percent: unknown,
  uid: string | undefined,
  bucketFor: (identifier: string) => number,
): boolean {
  // if the configured percent is unusable in any way, treat the feature as off — an
  // unreadable dial must not switch the resolver on. What counts as unusable, and why
  // a shape like `true` has to be refused before the conversion rather than after it,
  // is `readUsableRolloutPercent`.
  const asNumber = readUsableRolloutPercent(percent);
  if (asNumber === undefined) return false;
  // if there is no user, there is no stable cohort to place them in
  if (!uid) return false;
  return bucketFor(`conflict-resolve:${uid}`) < asNumber;
}

/**
 * Should this user's device restore closed log entries from its conflict archive? (#6847)
 *
 * The same reading as {@link shouldAutoResolveConflicts}: uid-keyed, every unusable percent reads
 * as 0. Its own prefix keeps this cohort independent of the resolver's, so the restore can be
 * widened before the resolver is switched back on.
 */
export function shouldRestoreFromConflictArchive(
  percent: unknown,
  uid: string | undefined,
  bucketFor: (identifier: string) => number,
): boolean {
  const asNumber = readUsableRolloutPercent(percent);
  if (asNumber === undefined) return false;
  if (!uid) return false;
  return bucketFor(`conflict-archive-restore:${uid}`) < asNumber;
}

/** Why a conflicted document was closed without merging, or that it was merged (#4314 R2). */
export enum ConflictMergeDecision {
  /** The merged body differs from the winner and should be written. */
  Merge = 'Merge',
  /** G1 — the document's parts must agree with each other, so it is never merged. */
  CloseOnlyStructural = 'CloseOnlyStructural',
  /** G2 — the two branches diverged too long ago to reconcile. */
  CloseOnlyTooOld = 'CloseOnlyTooOld',
  /** G2 — a write timestamp was missing or unparseable, so the gap cannot be computed. */
  CloseOnlyMissingStamp = 'CloseOnlyMissingStamp',
  /** G3 — merging changes nothing, so there is nothing to write. */
  CloseOnlyNoChange = 'CloseOnlyNoChange',
  /**
   * G2's exception for log entries (#6834): G2 would close the document, but a losing copy holds
   * more of the user's data than the shown one, so that copy is written as the winner, whole.
   */
  AdoptHigherRankedCopy = 'AdoptHigherRankedCopy',
}

/** What {@link decideConflictMergePolicy} concluded for one conflicted document. */
export interface ConflictMergePolicy {
  decision: ConflictMergeDecision;
  /** The body to write — present ONLY for {@link ConflictMergeDecision.Merge} and
   *  {@link ConflictMergeDecision.AdoptHigherRankedCopy}.
   *  Returned alongside the decision so the merge is computed exactly once and the
   *  caller's write path reuses it rather than merging again. */
  mergedBody?: any;
}

/**
 * Decide what to do with one conflicted document (#4314 R2).
 *
 * The three gates are applied in a deliberate order. G1 and G2 are decidable
 * BEFORE merging and are cheap, so they go first. G3 can only be decided AFTER
 * the merge has been computed, because it asks whether the merge changed
 * anything. Whatever the answer, the losing bodies are archived by the caller —
 * closing without merging still destroys a branch, so it is never a reason to
 * skip the archive.
 *
 * G2 note: a missing or unparseable `db__trans` on EITHER side closes without
 * merging, which is the opposite of what the four existing merge callers do.
 * That is deliberate. Those callers merge a live in-memory save that would
 * otherwise be DROPPED, so merging is the safe direction there. Here nothing is
 * dropped by closing — the winner is untouched and the losing body is archived —
 * so the safe direction reverses. It also matters concretely: the server never
 * writes `db__trans` at all, so a server-touched document has no stamp, and that
 * is exactly the case we can least reason about. The one exception is a log entry whose losing
 * copy holds more of the user's data than the shown one: closing would drop exactly that, so the
 * losing copy is written as the winner instead (#6834, see `pickCopyToAdoptOverWinner`).
 *
 * Note there is no "now" parameter: G2 gates the GAP between the two
 * branches' own write times, not how old either of them is, so the current time
 * never enters the decision.
 *
 * Multi-loser note: if ANY losing branch fails G2, the WHOLE document is closed
 * without merging rather than merging the branches that pass. This is the
 * conservative reading of a rule the plan states for a single branch, and it
 * costs little — about 10% of conflicted documents have more than one losing
 * branch, and every losing body is archived either way, so nothing is destroyed
 * unrecoverably by choosing not to merge.
 */
export function decideConflictMergePolicy(
  docId: string,
  winner: any,
  losingBodies: any[],
): ConflictMergePolicy {
  // G1: if this document's parts must agree with each other, never merge it
  if (CONFLICT_NO_MERGE_DOC_IDS.includes(docId)) {
    return { decision: ConflictMergeDecision.CloseOnlyStructural };
  }

  // G2: if the branches diverged too long ago, or a write time is unreadable, close without
  // merging, unless a losing copy holds more of the user's data than the shown one
  const ageGateDecision = decideAgeGate(winner, losingBodies);
  if (ageGateDecision !== undefined) {
    const adopted = pickCopyToAdoptOverWinner(winner, losingBodies);
    if (adopted !== undefined) {
      return { decision: ConflictMergeDecision.AdoptHigherRankedCopy, mergedBody: adopted };
    }
    return { decision: ageGateDecision };
  }

  // fold every losing body into the winner, winner first so its identity fields survive
  let merged = winner;
  for (const losing of losingBodies) {
    merged = mergeOneLosingBranch(merged, losing);
  }

  // G3: if the merge changed nothing there is nothing to write (deepCompareEQ
  // skips the `_` and `db__` keys, so this compares user content only)
  if (deepCompareEQ(merged, winner)) {
    return { decision: ConflictMergeDecision.CloseOnlyNoChange };
  }

  return { decision: ConflictMergeDecision.Merge, mergedBody: merged };
}

/**
 * Gate G2 on its own: the close-only decision it reaches, or `undefined` when it lets the
 * document through to the merge (#4314 R2).
 */
function decideAgeGate(
  winner: any,
  losingBodies: any[],
): ConflictMergeDecision.CloseOnlyMissingStamp | ConflictMergeDecision.CloseOnlyTooOld | undefined {
  // if either side's write time is unreadable, the gap cannot be computed
  const winnerMs = getTransactionTimeMs(winner);
  if (!Number.isFinite(winnerMs)) {
    return ConflictMergeDecision.CloseOnlyMissingStamp;
  }
  const maxGapMs = CONFLICT_MERGE_MAX_DIVERGENCE_DAYS * 24 * 60 * 60 * 1000;
  for (const losing of losingBodies) {
    const losingMs = getTransactionTimeMs(losing);
    if (!Number.isFinite(losingMs)) {
      return ConflictMergeDecision.CloseOnlyMissingStamp;
    }
    // Gate the GAP between the branches, not how old the document is: two
    // branches of an ancient document written days apart still merge, while a
    // branch the user superseded months ago does not.
    //
    // This is deliberately ONE-DIRECTIONAL. Only a loser far BEHIND the winner
    // is refused, because that is what the gate is for — a branch the user has
    // since superseded. A loser far AHEAD of the winner is still merged, and
    // that case is reachable: CouchDB picks its winner by revision depth and
    // rev-id, never by time, so a branch written later can lose. Merging it is
    // the right call — it is the user's own newer writing, which they have never
    // seen — but it does mean the visible document can change by more than the
    // 14 days this number suggests.
    if (winnerMs - losingMs > maxGapMs) {
      return ConflictMergeDecision.CloseOnlyTooOld;
    }
  }
  return undefined;
}

/**
 * The losing copy of a log entry to write as the winner instead of letting G2 close it, if any
 * (#6834): the one that ranks above the shown copy by `logEntryCopyRank`, taken whole rather than
 * merged. Among losers of the top rank the newest write wins, and with no readable stamp the first
 * in the list does. The body carries the winner's `_id` and `_rev`, so the write lands on top of
 * the shown revision. Why, with the real case: `design-docs/sync-integrity.md`, G2's exception.
 */
function pickCopyToAdoptOverWinner(winner: any, losingBodies: any[]): any | undefined {
  if (!isLogRecordEntry(winner)) return undefined;
  const winnerRank = logEntryCopyRank(winner, logEntryHoldsUserData(winner));
  let best: any | undefined;
  let bestRank = winnerRank;
  for (const losing of losingBodies) {
    if (!isLogRecordEntry(losing)) continue;
    const rank = logEntryCopyRank(losing, logEntryHoldsUserData(losing));
    // take it if it outranks the best so far, or ties it and was written later
    if (rank > bestRank || (best !== undefined && rank === bestRank && isSecondDocNewer(best, losing))) {
      best = losing;
      bestRank = rank;
    }
  }
  if (best === undefined) return undefined;
  const adopted = deepClone(best);
  adopted._id = winner._id;
  adopted._rev = winner._rev;
  return adopted;
}

/**
 * Merge one losing branch into the body folded so far (#6825).
 *
 * The plain merge settles every tie for the whole document by whichever version was written last.
 * For a log entry that is wrong when one version is an empty placeholder: a device that starts
 * before its first sync saves its own `Scheduled` copy of today's weigh-in, and that copy is newer
 * than the one the user logged an hour earlier on their phone. It then won `state`, and the merge
 * reopened the user's completed weigh-in.
 *
 * So for two log entries the tie goes to the version that speaks more for the user, by the same
 * rank every screen uses to choose between two copies of a day's entry (`logEntryCopyRank`), and
 * only versions of equal rank fall back to the newer write. It is compared against the body folded
 * so far, not the original winner, so once a user's completed copy has been folded in, a later
 * placeholder loses its ties too. Anything that is not a log entry merges exactly as before.
 */
function mergeOneLosingBranch(mergedSoFar: any, losing: any): any {
  if (!isLogRecordEntry(mergedSoFar) || !isLogRecordEntry(losing)) {
    return mergeConflictingDocs(mergedSoFar, losing);
  }
  const mergedRank = logEntryCopyRank(mergedSoFar, logEntryHoldsUserData(mergedSoFar));
  const losingRank = logEntryCopyRank(losing, logEntryHoldsUserData(losing));
  const preferLosingOnTie = losingRank !== mergedRank
    ? losingRank > mergedRank
    : isSecondDocNewer(mergedSoFar, losing);
  return mergeConflictingDocsPreferring(mergedSoFar, losing, preferLosingOnTie);
}

/**
 * The `db__trans` stamp a merged winner is written with (#4314 R4).
 *
 * The MAXIMUM of the winner's and every merged loser's write time, never "now".
 * A janitorial "now" stamp would make this cleanup look like the newest write
 * the document ever received, so resurrected old content would beat the user's
 * NEXT genuine edit in every tie-break — the recency comparison reads only the
 * top-level stamp. Since the merge already carries the winner's `db__*` fields
 * through, this only ever moves the stamp when a loser's was newer.
 */
export function resolveMergedTransactionStamp(
  winner: any,
  losingBodies: any[],
  deviceId: string,
): string | undefined {
  let bestMs = getTransactionTimeMs(winner);
  let best: string | undefined = typeof winner?.db__trans === 'string' ? winner.db__trans : undefined;
  for (const losing of losingBodies) {
    const losingMs = getTransactionTimeMs(losing);
    // if this loser was written later than the best so far, it sets the stamp
    if (Number.isFinite(losingMs) && (!Number.isFinite(bestMs) || losingMs > bestMs)) {
      bestMs = losingMs;
      best = losing?.db__trans;
    }
  }
  if (!Number.isFinite(bestMs) || !best) return undefined;
  // rewrite the device half to THIS device while keeping the winning time
  const isoPart = String(best).split('|')[0];
  return `${isoPart}|${deviceId}`;
}

/** Whether the conflict scan should run now, and if so in which role (#4314 R6). */
export interface ConflictScanRunDecision {
  shouldRun: boolean;
  /**
   * True when this run continues a drain that a previous run stopped part-way
   * through. A continuation must NOT re-fire `sync_conflict_scan` as a fresh
   * daily sample: it is a once-per-device-per-day metric and the ramp's cohort
   * comparison depends on that cadence.
   */
  isContinuation: boolean;
}

/**
 * Decide whether to run the conflict scan now (#4314 R6).
 *
 * Two ways in. The ordinary one is the 24-hour cooldown that Phase B already
 * used, which keeps its meaning exactly so devices upgrading into this change do
 * not all re-scan at once. The second is the resume marker: when a run stops with
 * work still to do AND it made progress, it leaves a marker so the next
 * six-minute health tick picks the drain straight back up instead of waiting a
 * day. A run that resolved nothing writes no marker and falls back to the daily
 * cooldown, so the device always either makes progress or backs off — an
 * infinite retry loop is not reachable.
 *
 * An unparseable timestamp on either key is treated as absent. For the last-scan
 * key that means "scan now", which matches what Phase B already did; for the
 * resume marker it means "no continuation pending".
 */
export function decideConflictScanRun(
  lastScanIso: string | undefined,
  drainResumeAfterIso: string | undefined,
  nowMs: number,
): ConflictScanRunDecision {
  // if a drain was left part-way through and its resume time has arrived, continue it
  if (drainResumeAfterIso) {
    const resumeMs = DateTime.fromISO(drainResumeAfterIso).toMillis();
    if (Number.isFinite(resumeMs) && nowMs >= resumeMs) {
      return { shouldRun: true, isContinuation: true };
    }
  }

  // otherwise fall back to the daily cadence. An unreadable last-scan time scans
  // now rather than holding back — `Allow` — which is what Phase B did and what
  // stops a device with a damaged value never scanning again.
  const dueForADailyScan = mayHappenAgainSinceStored(
    lastScanIso,
    { hours: CONFLICT_SCAN_COOLDOWN_HOURS },
    DateTime.fromMillis(nowMs),
    WhenMomentUnreadable.Allow,
  );
  return { shouldRun: dueForADailyScan, isContinuation: false };
}

/** Why a resolver run stopped, or that it may keep going (#4314 R6/R6b). */
export enum ConflictRunStep {
  /** Keep going — take the next document. */
  Continue = 'Continue',
  /** The work-time budget is spent. */
  StopWorkBudget = 'StopWorkBudget',
  /** The per-run document ceiling was reached. */
  StopDocumentCap = 'StopDocumentCap',
  /** A condition the run requires stopped holding — stop immediately. */
  StopAborted = 'StopAborted',
}

/** The live conditions a resolver run re-checks between documents (#4314 R6b.4). */
export interface ConflictRunConditions {
  /** Accumulated ACTUAL WORK time, excluding the deliberate pauses between documents. */
  workMs: number;
  /** Accumulated wall-clock time, pauses included. Reported in `sync_conflicts_resolved`
   *  as `wall_ms`, never used as the budget — see the work-budget constant for why. */
  wallMs: number;
  /** How many documents this run has attempted so far. */
  documentsAttempted: number;
  appVisible: boolean;
  online: boolean;
  signedIn: boolean;
  /** True while a coach/admin impersonation database is in use — reads route elsewhere. */
  impersonating: boolean;
  /** True once the database read/write lock has begun shutting down. */
  shuttingDown: boolean;
  /** True while the local database is being recreated underneath us. */
  databaseRecreating: boolean;
}

/**
 * Decide whether a resolver run may take another document (#4314 R6b).
 *
 * The abort conditions are checked FIRST and on every document, not just at the
 * start of the run. With a pause between documents a run spans a long stretch of
 * wall-clock time, and two of these matter more than they look: the enclosing
 * scan holds a read lock for the whole run, acquiring the write lock waits for
 * ALL readers, and a held read cannot be revoked. So a slow yielding run directly
 * delays shutdown, sign-out and database recreation until it finishes. Aborting
 * on those signals is what keeps the read-hold short in practice.
 *
 * The minimum-one-document floor is the anti-deadlock rule and it deliberately
 * OVERRIDES an already-spent work budget. Without it, a device whose enumerating
 * sweep is slow could arrive here with the budget gone, resolve nothing, and be
 * backed off for 24 hours by the "made no progress" rule — every day, for ever,
 * on exactly the heaviest accounts. The floor guarantees forward progress, which
 * is what makes "resolved nothing" mean a real failure rather than an accounting
 * artefact. Note the floor never overrides an ABORT: if the user signed out or
 * the database is being torn down, the run stops even on its first document.
 */
export function decideConflictRunStep(conditions: ConflictRunConditions): ConflictRunStep {
  const {
    workMs, documentsAttempted, appVisible, online, signedIn,
    impersonating, shuttingDown, databaseRecreating,
  } = conditions;

  // if anything the run depends on stopped holding, stop right now — this
  // outranks the progress floor below
  if (!appVisible || !online || !signedIn) return ConflictRunStep.StopAborted;
  if (impersonating || shuttingDown || databaseRecreating) return ConflictRunStep.StopAborted;

  // the document ceiling is a hard backstop and is never overridden
  if (documentsAttempted >= CONFLICT_RESOLVE_MAX_DOCS_PER_RUN) return ConflictRunStep.StopDocumentCap;

  // always attempt at least one document, even if the budget is already spent
  if (documentsAttempted === 0) return ConflictRunStep.Continue;

  if (workMs >= CONFLICT_RESOLVE_WORK_BUDGET_MS) return ConflictRunStep.StopWorkBudget;
  return ConflictRunStep.Continue;
}
